import {
  useAddContactToMarketingListMutation,
  useCreatePendingAccountMutation,
  useLazyGetReferalPartnerDetailsQuery,
  useSubmitPhoneNumberMutation,
  useVerifyPhoneNumberMutation,
  useGetPublicCurrentLegalDocumentQuery,
} from "src/store/features/api/generated";
import { usStateCode } from "src/utils/usStates";
import { trackEvent } from "src/Amplitude";
import {
  setCountrySearch,
  setIsCountryDropdownOpen,
  setIsStateDropdownOpen,
  setStateSearch,
  setCountry,
  setEmail,
  setLastName,
  setPassword,
  setConfirmPassword,
  setState,
  setFirstName,
  setIsPasswordVisible,
  setIsConfirmPasswordVisible,
  setStateError,
  setCountryError,
  setFirstNameError,
  setLastNameError,
  setEmailError,
  setPasswordError,
  setConfirmPasswordError,
  setIsLoadingSubmitPending,
  setUniqueKey,
  setSubmitErrorMessage,
  setIsLoadingSubscribe,
  setReferralDetails,
  setPhoneError,
  setPhone,
  setPhoneVerificationCodeError,
  setPhoneVerificationCode,
  setPhoneCountry,
  setIsPhoneCountryDropdownOpen,
  setIsLoadingPhoneNumber,
} from "src/store/features/signUpFormInput.slice";
import { useSignUpFormInputSlice } from "src/store/features/signUpFormInput.slice";
import { useTurnstileSlice } from "src/store/features/turnstile.slice";
import { useAppDispatch } from "src/store/store";
import { v4 as uuidv4 } from "uuid";
import {
  resetSubscribeGroups,
  setSubscribeGroups,
  SubscribeGroupType,
  useSubscribeState,
} from "src/store/features/subscribe.slice";
import useSupportedCountries from "src/utils/hooks/useSupportedCountries";
import {
  PHONE_VALIDATION_STAGE,
  setPhoneValidationStage,
  setSignUpStage,
  setSubscriptionStage,
  SIGN_UP_STAGE,
  SUBSCRIPTION_STAGE,
} from "src/store/features/signUpForm.slice";
import { useEffect, useRef, useState } from "react";
import * as Yup from "yup";
import { resetTurnstile } from "src/store/features/turnstile.slice";
import { useIsMobile } from "src/utils/hooks/useIsMobile";
import type { DisclosureStatus } from "src/components/disclosures/disclosure.types";

// Validation schema for the sign-up form
const SignUpValidationSchema = Yup.object().shape({
  country: Yup.string().required("Country is required"),
  countrySearch: Yup.string().when("country", {
    is: (country: string) => country && country.length > 0,
    then: Yup.string().test(
      "country-match",
      "Please select a country from the dropdown",
      function (value) {
        const { country } = this.parent;
        return !value || country === value;
      },
    ),
    otherwise: Yup.string(),
  }),
  state: Yup.string().when("country", {
    is: "United States",
    then: Yup.string().required("State is required"),
    otherwise: Yup.string().nullable(),
  }),
  firstName: Yup.string()
    .trim()
    .required("First name is required")
    .matches(/^[a-zA-Z\s]*$/, "Only letters and spaces are allowed")
    .max(50, "First name must be at most 50 characters long"),
  lastName: Yup.string()
    .trim()
    .required("Last name is required")
    // eslint-disable-next-line no-useless-escape
    .matches(/^[a-zA-Z\s\-]+$/, "Only letters and spaces are allowed")
    .max(50, "Last name must be at most 50 characters long"),
  email: Yup.string()
    .email("Must be a valid email")
    .min(3, "Email must be at least 3 characters")
    .max(50, "Email must be less than 50 characters")
    .required("Email is required"),
  password: Yup.string()
    .matches(/^(?=.*[a-z]).+$/, "Password must contain at least one lowercase letter")
    .matches(/^(?=.*[A-Z]).+$/, "Password must contain at least one uppercase letter")
    .matches(/^(?=.*\d).+$/, "Password must contain at least one digit")
    .matches(
      /^(?=.*[*.!@$%^&(){}[\]:;<>,.?/~+\-=|\\_]).+$/,
      "Password must contain at least one special character",
    )
    .min(8, "Password must be at least 8 characters")
    .max(255, "Password must be less than 255 characters")
    .required("Password is required"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("password"), null], "Please make sure both passwords are the same")
    .required("Confirm password is required"),
});

export function useSignUpForm() {
  const dispatch = useAppDispatch();
  const [countdownSeconds, setCountdownSeconds] = useState<number | null>(null);

  const {
    isCountryDropdownOpen,
    isStateDropdownOpen,
    isPasswordVisible,
    isConfirmPasswordVisible,
    referralDetails,
    uniqueKey,
    country,
    countrySearch,
    state,
    firstName,
    lastName,
    email,
    password,
    confirmPassword,
    countryError,
    stateError,
    firstNameError,
    lastNameError,
    emailError,
    passwordError,
    confirmPasswordError,
    phone,
    phoneError,
    phoneVerificationCode,
    phoneVerificationCodeError,
  } = useSignUpFormInputSlice();

  // Signup is pre-auth: resolve the state disclosure from the public endpoint. 200 → required, 404 → not.
  const signupStateRegion = usStateCode(state);
  const signupStateQuery = useGetPublicCurrentLegalDocumentQuery(
    { type: "state-disclosure", country: "US", region: signupStateRegion },
    { skip: !signupStateRegion },
  );
  const signupStateDoc = signupStateQuery.data;
  const signupStateDisclosureVersion = signupStateDoc?.version;
  const signupStateNotRequired =
    signupStateQuery.isError &&
    typeof signupStateQuery.error === "object" &&
    signupStateQuery.error !== null &&
    "status" in signupStateQuery.error &&
    (signupStateQuery.error as { status?: unknown }).status === 404;

  const { turnstileToken } = useTurnstileSlice();

  const { signupSources } = useSubscribeState();
  const { isCountrySupported, allCountries } = useSupportedCountries();

  // Get device type for tracking
  const isMobile = useIsMobile();
  const device = isMobile ? "mobile" : "desktop";

  /*************
   * API CALLS *
   *************/
  const [createPendingAccount] = useCreatePendingAccountMutation();
  const [addCustomerToMarketingList] = useAddContactToMarketingListMutation();
  const [getPartnershipInfo] = useLazyGetReferalPartnerDetailsQuery();
  const [submitPhoneNumber] = useSubmitPhoneNumberMutation();
  const [verifyPhoneNumber] = useVerifyPhoneNumberMutation();

  /*************
   * HANDLERS *
   *************/
  const handleCountrySearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    dispatch(setCountrySearch(value));
    if (!isCountryDropdownOpen) {
      dispatch(setIsCountryDropdownOpen(true));
    }
  };

  const handleCountryChange = (value: string) => {
    dispatch(setCountry(value));
    if (!!value && countryError.includes("is required")) {
      dispatch(setCountryError(""));
    }
    if (!isCountrySupported(value)) {
      dispatch(setSignUpStage(SIGN_UP_STAGE.UNSUPPORTED_COUNTRY));
    }

    // Set phone country based on selected country of residence using the same data source
    const selectedCountry = allCountries.find((country) => country.label === value);
    if (selectedCountry) {
      dispatch(setPhoneCountry(selectedCountry.value.toLowerCase()));
    }
  };

  const toggleCountryDropdown = (visible: boolean) => {
    dispatch(setIsCountryDropdownOpen(visible));
  };

  const handleStateSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    dispatch(setStateSearch(value));
    if (!isStateDropdownOpen) {
      dispatch(setIsStateDropdownOpen(true));
    }
  };

  const handleStateChange = (value: string) => {
    dispatch(setState(value));
    if (!!value && stateError.includes("is required")) {
      dispatch(setStateError(""));
    }
    if (value === "New York") {
      dispatch(setSignUpStage(SIGN_UP_STAGE.UNSUPPORTED_COUNTRY));
    } else if (value === "South Dakota") {
      dispatch(setSignUpStage(SIGN_UP_STAGE.UNSUPPORTED_COUNTRY));
    }
  };

  const toggleStateDropdown = (visible: boolean) => {
    dispatch(setIsStateDropdownOpen(visible));
  };

  const handleFirstNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    dispatch(setFirstName(value));

    // Clear "is required" error when user starts typing
    if (!!value && firstNameError === "First name is required") {
      dispatch(setFirstNameError(""));
    }

    // Validate in real-time and clear errors when validation passes
    if (!!value) {
      const isValid = /^[a-zA-Z\s]*$/.test(value) && value.length <= 50;
      if (isValid && firstNameError) {
        dispatch(setFirstNameError(""));
      }
    }
  };

  const handleLastNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    dispatch(setLastName(value));

    // Clear "is required" error when user starts typing
    if (!!value && lastNameError === "Last name is required") {
      dispatch(setLastNameError(""));
    }

    // Validate in real-time and clear errors when validation passes
    if (!!value) {
      // eslint-disable-next-line no-useless-escape
      const isValid = /^[a-zA-Z\s\-]+$/.test(value) && value.length <= 50;
      if (isValid && lastNameError) {
        dispatch(setLastNameError(""));
      }
    }
  };

  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    const userInput = value.toLowerCase();

    dispatch(setEmail(userInput));
    if (!!userInput && emailError.includes("is required")) {
      dispatch(setEmailError(""));
    }

    // Clear previous debounce timeout
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    if (userInput.length > 0 && userInput.includes("@") && userInput.includes(".")) {
      debounceTimer.current = setTimeout(() => {
        try {
          const { isValid, error } = validateEmail(userInput);
          if (isValid) {
            dispatch(setEmailError(""));
          } else {
            dispatch(setEmailError(error || "Email is invalid"));
          }
        } catch (error: any) {
          dispatch(setEmailError(error.message || "Email is invalid"));
        }
      }, 500);
    }
  };

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    dispatch(setPassword(value));
    if (!!value && !!passwordError) {
      dispatch(setPasswordError(""));
    }
    if (
      !!value &&
      confirmPasswordError.includes("Please make sure both passwords are the same") &&
      confirmPassword === value
    ) {
      dispatch(setConfirmPasswordError(""));
    }
  };

  const handleConfirmPasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    dispatch(setConfirmPassword(value));
    if (!!value && confirmPasswordError.includes("is required")) {
      dispatch(setConfirmPasswordError(""));
    }
    if (
      !!value &&
      confirmPasswordError.includes("Please make sure both passwords are the same") &&
      password === value
    ) {
      dispatch(setConfirmPasswordError(""));
    }
  };

  const handlePhoneChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    dispatch(setPhone(value));
    if (!!value && phoneError.includes("is required")) {
      dispatch(setPhoneError(""));
    }
  };

  const handlePhoneVerificationCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    // Only allow numeric characters
    const numericValue = value.replace(/[^0-9]/g, "");

    if (numericValue.length > 6) {
      return;
    }
    dispatch(setPhoneVerificationCode(numericValue));
    if (!!numericValue && phoneVerificationCodeError.includes("is required")) {
      dispatch(setPhoneVerificationCodeError(""));
    }
  };

  const handlePhoneCountryChange = (country: string) => {
    dispatch(setPhoneCountry(country));
  };

  const togglePhoneCountryDropdown = (visible: boolean) => {
    dispatch(setIsPhoneCountryDropdownOpen(visible));
  };

  const togglePasswordVisibility = () => {
    dispatch(setIsPasswordVisible(!isPasswordVisible));
  };

  const toggleConfirmPasswordVisibility = () => {
    dispatch(setIsConfirmPasswordVisible(!isConfirmPasswordVisible));
  };

  // ---------- SIGNUP FORM SUBMIT ----------
  const resetErrors = () => {
    dispatch(setSubmitErrorMessage(""));
    dispatch(setCountryError(""));
    dispatch(setStateError(""));
    dispatch(setFirstNameError(""));
    dispatch(setLastNameError(""));
    dispatch(setEmailError(""));
    dispatch(setPasswordError(""));
    dispatch(setConfirmPasswordError(""));
  };

  const resetInputs = () => {
    dispatch(setCountry(""));
    dispatch(setCountrySearch(""));
    dispatch(setState(""));
    dispatch(setStateSearch(""));
    dispatch(setFirstName(""));
    dispatch(setLastName(""));
    dispatch(setEmail(""));
    dispatch(setPassword(""));
    dispatch(setConfirmPassword(""));
  };

  const resetPhoneInputs = () => {
    dispatch(setPhone(""));
    dispatch(setPhoneError(""));
    dispatch(setIsPhoneCountryDropdownOpen(false));
    dispatch(setPhoneVerificationCode(""));
    dispatch(setPhoneVerificationCodeError(""));
  };

  const resetForm = () => {
    // Reset all form inputs
    resetInputs();
    // Reset all errors
    resetErrors();
    // Reset phone inputs
    resetPhoneInputs();
    // Reset password visibility states
    dispatch(setIsPasswordVisible(false));
    dispatch(setIsConfirmPasswordVisible(false));
    // Reset dropdown states
    dispatch(setIsCountryDropdownOpen(false));
    dispatch(setIsStateDropdownOpen(false));
    // Reset form stage to initial
    dispatch(setSignUpStage(SIGN_UP_STAGE.INITIAL));
    dispatch(setPhoneValidationStage(PHONE_VALIDATION_STAGE.INPUT));
    dispatch(setSubscriptionStage(SUBSCRIPTION_STAGE.SUBSCRIPTION));
    // Reset loading states
    dispatch(setIsLoadingSubmitPending(false));
    dispatch(setIsLoadingPhoneNumber(false));
    dispatch(setIsLoadingSubscribe(false));
    // Reset referral details
    resetReferralDetails();
    // Reset countdown
    setCountdownSeconds(null);
    // Reset turnstile state
    dispatch(resetTurnstile());
  };

  const capitaliseFirstLetters = (str: string) => {
    return str
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  // Unresolved lookups still show the stage — never silently skip a required disclosure
  const requiresDisclosure = (): boolean =>
    signupStateRegion != null && !signupStateNotRequired;

  // Build state disclosure payload when user completed disclosure during sign-up
  const buildSignUpStateDisclosures = (
    acceptedDisclosures?: Record<string, DisclosureStatus>,
  ): { state: string; accepted: boolean; timestamp: number; disclosureVersion?: string }[] | undefined => {
    if (!signupStateRegion || !acceptedDisclosures) return undefined;
    // Don't record acceptance unless the disclosure actually loaded (has a version).
    if (signupStateDisclosureVersion == null) return undefined;
    return [
      {
        // ISO region code ("CA"), same convention as native
        state: signupStateRegion,
        accepted: true,
        timestamp: Date.now(),
        disclosureVersion: signupStateDisclosureVersion,
      },
    ];
  };

  // Process the actual signup after disclosures are accepted
  const processSignup = async (acceptedDisclosures?: Record<string, DisclosureStatus>) => {
    try {
      dispatch(setIsLoadingSubmitPending(true));

      // Generate a unique key for the account so it can be used to identify the account in the backend
      const newUniqueKey = uuidv4();
      dispatch(setUniqueKey(newUniqueKey));

      const stateDisclosures = buildSignUpStateDisclosures(acceptedDisclosures);

      await createPendingAccount({
        createPendingAccountDto: {
          uniqueKey: newUniqueKey,
          country,
          stateName: state,
          firstName: capitaliseFirstLetters(firstName),
          lastName: capitaliseFirstLetters(lastName),
          email: email.toLowerCase(),
          referrer: signupSources?.referrer ?? null,
          utm_source: signupSources?.utm_source ?? null,
          utm_medium: signupSources?.utm_medium ?? null,
          utm_campaign: signupSources?.utm_campaign ?? null,
          gclid__c: signupSources?.gclid__c ?? null,
          ...(stateDisclosures != null && stateDisclosures.length > 0 && { stateDisclosures }),
        },
      }).unwrap();

      // Track successful lead creation
      trackEvent(
        "Lead created",
        {
          firstName,
          country,
          ...signupSources,
          state,
          email,
          device,
        },
        "Sign_Up",
      );

      window.dataLayer.push({
        event: "signup_completed",
      });

      resetPhoneInputs();
      dispatch(resetTurnstile());
      dispatch(setSignUpStage(SIGN_UP_STAGE.PHONE));
      dispatch(setPhoneValidationStage(PHONE_VALIDATION_STAGE.INPUT));
    } catch (error: any) {
      console.error(error);
      if (error?.message?.includes("Please enter a valid email address.")) {
        dispatch(setEmailError("Please enter a valid email address."));
      } else {
        dispatch(
          setSubmitErrorMessage(
            "An error occurred while creating your account, please contact us at support@calebandbrown.com for assistance.",
          ),
        );
      }
    } finally {
      dispatch(setIsLoadingSubmitPending(false));
    }
  };

  // Handle disclosure flow completion (receives accepted disclosures from DisclosureFlow)
  const handleDisclosureComplete = async (acceptedDisclosures: Record<string, DisclosureStatus>) => {
    dispatch(setSignUpStage(SIGN_UP_STAGE.INITIAL));
    await processSignup(acceptedDisclosures);
  };

  // Handle disclosure flow close (user cancelled)
  const handleDisclosureClose = () => {
    dispatch(setSignUpStage(SIGN_UP_STAGE.INITIAL));
  };

  const handleSubmit = async () => {
    try {
      resetErrors();

      // Prepare form data for validation
      const formData = {
        country,
        countrySearch,
        state,
        firstName,
        lastName,
        email,
        password,
        confirmPassword,
      };

      // Validate form data using Yup schema
      await SignUpValidationSchema.validate(formData, { abortEarly: false });

      // Check if disclosure is required based on user's state
      if (requiresDisclosure()) {
        dispatch(setSignUpStage(SIGN_UP_STAGE.DISCLOSURE));
        return;
      }

      // No disclosure required, proceed with signup
      await processSignup();
    } catch (error: any) {
      console.error(error);

      if (error.name === "ValidationError") {
        // Handle Yup validation errors
        const validationErrors = error.inner.reduce((acc: any, err: any) => {
          acc[err.path] = err.message;
          return acc;
        }, {});

        // Set validation errors in the store
        if (validationErrors.country) dispatch(setCountryError(validationErrors.country));
        if (validationErrors.countrySearch)
          dispatch(setCountryError(validationErrors.countrySearch));
        if (validationErrors.state) dispatch(setStateError(validationErrors.state));
        if (validationErrors.firstName) dispatch(setFirstNameError(validationErrors.firstName));
        if (validationErrors.lastName) dispatch(setLastNameError(validationErrors.lastName));
        if (validationErrors.email) dispatch(setEmailError(validationErrors.email));
        if (validationErrors.password) dispatch(setPasswordError(validationErrors.password));
        if (validationErrors.confirmPassword)
          dispatch(setConfirmPasswordError(validationErrors.confirmPassword));
      } else if (error?.message?.includes("Please enter a valid email address.")) {
        dispatch(setEmailError("Please enter a valid email address."));
      } else {
        dispatch(
          setSubmitErrorMessage(
            "An error occurred while creating your account, please contact us at support@calebandbrown.com for assistance.",
          ),
        );
      }
    }
  };

  // ---------- SUBSCRIPTION ----------
  const handleSubscribeSubmit = async (listNames: SubscribeGroupType[]) => {
    if (listNames.length === 0) {
      dispatch(setSubscriptionStage(SUBSCRIPTION_STAGE.SUBSCRIPTION_NOT_NOW));
      return;
    }

    try {
      dispatch(setIsLoadingSubscribe(true));

      await addCustomerToMarketingList({
        sendgridContactToListDto: {
          email,
          listNames,
        },
      });

      // Track subscription success
      trackEvent(
        "Subscription success",
        {
          email,
          listNames,
          ...signupSources,
          device,
        },
        "Sign_Up",
      );

      dispatch(setSubscriptionStage(SUBSCRIPTION_STAGE.SUBSCRIPTION_SUCCESS));
    } catch (error) {
      console.error(error);
    } finally {
      dispatch(setIsLoadingSubscribe(false));
    }
  };

  const handleSubscribeOptionClick = (group: SubscribeGroupType) => {
    dispatch(setSubscribeGroups(group));
  };

  const handleSubscribeNotNow = () => {
    // Track subscription not now
    trackEvent(
      "Subscription not now",
      {
        email,
        ...signupSources,
        device,
      },
      "Sign_Up",
    );

    dispatch(setSubscriptionStage(SUBSCRIPTION_STAGE.SUBSCRIPTION_NOT_NOW));
  };

  const handleUnsupportedCountryGoBack = () => {
    dispatch(setSignUpStage(SIGN_UP_STAGE.INITIAL));
    dispatch(setCountry(""));
    dispatch(setCountrySearch(""));
    dispatch(setState(""));
    dispatch(setStateSearch(""));
  };

  const resetReferralDetails = () => {
    dispatch(setReferralDetails({ referrer: null, name: null, rewards: null }));
  };

  // ---------- PHONE VALIDATION ----------
  const handleSignUpFormExpired = () => {
    dispatch(setSignUpStage(SIGN_UP_STAGE.INITIAL));
    dispatch(setPhoneValidationStage(PHONE_VALIDATION_STAGE.INPUT));
    dispatch(setSubmitErrorMessage("Sign up form has expired, please re-submit."));
    resetPhoneInputs();
    resetInputs();
  };

  const handleSubmitPhoneNumber = async (resend = false) => {
    if (!phone) {
      dispatch(setPhoneError("Phone number is required"));
      return;
    }

    if (!turnstileToken) {
      dispatch(setPhoneError("Security verification is required"));
      return;
    }

    try {
      dispatch(setPhoneError(""));
      dispatch(setIsLoadingPhoneNumber(true));

      await submitPhoneNumber({
        submitPendingAccountPhoneNumberDto: {
          uniqueKey,
          phoneNumber: phone,
          email,
          turnstileToken,
        },
      }).unwrap();

      // Track phone submission
      trackEvent(
        "Phone submission",
        {
          email,
          phone,
          resend,
          ...signupSources,
          device,
        },
        "Sign_Up",
      );

      dispatch(resetTurnstile());
      dispatch(
        setPhoneValidationStage(
          resend ? PHONE_VALIDATION_STAGE.CODE_RESENT : PHONE_VALIDATION_STAGE.CODE_SENT,
        ),
      );
    } catch (error: any) {
      console.error(error);

      // Check if it's a "Too many phone number submissions" error
      if (
        error?.message?.includes("Too many phone number submissions") ||
        error?.message?.includes("Too many requests")
      ) {
        const message = error.message;
        const secondsMatch = message.match(/(\d+) seconds/);
        if (secondsMatch) {
          const seconds = parseInt(secondsMatch[1], 10);
          setCountdownSeconds(seconds);
        } else {
          // too many requests is a guard error, so it wont have a time to wait, set to 2 minutes
          setCountdownSeconds(60 * 2);
        }
        dispatch(
          setPhoneError("Too many phone number submissions. Please wait before trying again."),
        );
        if (resend) {
          dispatch(setPhoneValidationStage(PHONE_VALIDATION_STAGE.INPUT));
        }
      } else if (error?.message?.includes("Sign up form has expired.")) {
        handleSignUpFormExpired();
      } else {
        dispatch(
          setPhoneError(
            "Invalid phone number, please try again or contact us at support@calebandbrown.com for assistance.",
          ),
        );
      }
    } finally {
      dispatch(setIsLoadingPhoneNumber(false));
    }
  };

  const handleResendPhoneNumber = async () => {
    // Don't allow resend if countdown is active
    if (countdownSeconds !== null && countdownSeconds > 0) {
      return;
    }

    dispatch(setPhoneVerificationCode(""));
    dispatch(setPhoneVerificationCodeError(""));
    await handleSubmitPhoneNumber(true);
  };

  const handleVerifyPhoneNumber = async () => {
    if (!phoneVerificationCode) {
      dispatch(setPhoneVerificationCodeError("Phone verification code is required"));
      return;
    }

    if (!turnstileToken) {
      dispatch(setPhoneVerificationCodeError("Security verification is required"));
      return;
    }

    try {
      dispatch(setPhoneVerificationCodeError(""));
      dispatch(setIsLoadingPhoneNumber(true));

      await verifyPhoneNumber({
        verifyPendingAccountPhoneNumberDto: {
          uniqueKey,
          phoneNumber: phone,
          email,
          password,
          otp: phoneVerificationCode,
          turnstileToken,
        },
      }).unwrap();

      // Track phone validation success
      trackEvent(
        "Phone validation success",
        {
          email,
          phone,
          ...signupSources,
          device,
        },
        "Sign_Up",
      );

      // Reset password and confirm password so it doesnt stay in the store
      dispatch(setPassword(""));
      dispatch(setConfirmPassword(""));
      resetPhoneInputs();
      dispatch(resetSubscribeGroups());
      dispatch(setSignUpStage(SIGN_UP_STAGE.SUBSCRIPTION));
      dispatch(setPhoneValidationStage(PHONE_VALIDATION_STAGE.INPUT));
    } catch (error: any) {
      console.error(error);

      // Track phone validation failure
      trackEvent(
        "Phone validation failure",
        {
          email,
          phone,
          error: error?.message || "Unknown error",
          ...signupSources,
          device,
        },
        "Sign_Up",
      );

      if (error?.message?.includes("Too many checks.")) {
        dispatch(
          setPhoneVerificationCodeError("Too many submission attempts, please resend code here"),
        );
      } else if (error?.message?.includes("Sign up form has expired.")) {
        handleSignUpFormExpired();
      } else {
        dispatch(
          setPhoneVerificationCodeError("Invalid code, please try again or resend code here"),
        );
      }
    } finally {
      dispatch(setIsLoadingPhoneNumber(false));
    }
  };

  const handlePhoneInputGoBack = () => {
    dispatch(setSignUpStage(SIGN_UP_STAGE.INITIAL));
    dispatch(setPhoneValidationStage(PHONE_VALIDATION_STAGE.INPUT));
    resetPhoneInputs();
  };

  const handlePhoneValidationGoBack = () => {
    dispatch(setPhoneValidationStage(PHONE_VALIDATION_STAGE.INPUT));
    dispatch(setPhoneError(""));
    dispatch(setPhoneVerificationCode(""));
    dispatch(setPhoneVerificationCodeError(""));
  };

  /*************
   * VALIDATION HELPERS *
   *************/
  const validatePassword = (passwordValue: string) => {
    try {
      // Create a partial schema just for password validation
      const passwordSchema = Yup.object().shape({
        password: Yup.string()
          .matches(/^(?=.*[a-z]).+$/, "Password must contain at least one lowercase letter")
          .matches(/^(?=.*[A-Z]).+$/, "Password must contain at least one uppercase letter")
          .matches(/^(?=.*\d).+$/, "Password must contain at least one digit")
          .matches(
            /^(?=.*[*.!@$%^&(){}[\]:;<>,.?/~+\-=|\\_]).+$/,
            "Password must contain at least one special character",
          )
          .min(8, "Password must be at least 8 characters")
          .max(255, "Password must be less than 255 characters")
          .required("Password is required"),
      });

      passwordSchema.validateSync({ password: passwordValue });
      return { isValid: true, errors: {} };
    } catch (error: any) {
      if (error.name === "ValidationError") {
        const errors = error.inner.reduce((acc: any, err: any) => {
          acc[err.path] = err.message;
          return acc;
        }, {});
        return { isValid: false, errors };
      }
      return { isValid: false, errors: { password: "Validation failed" } };
    }
  };

  const getPasswordValidationResults = (passwordValue: string) => {
    const hasLowercaseOrUppercase = /[a-z]/.test(passwordValue) && /[A-Z]/.test(passwordValue);
    const has8Characters = passwordValue.length >= 8;
    // Use the same regex pattern as the Yup schema for consistency
    const hasSpecialCharacter = /[*.!@$%^&(){}[\]:;<>,.?/~+\-=|\\_]/.test(passwordValue);
    const hasNumber = /[0-9]/.test(passwordValue);

    return {
      hasLowercaseOrUppercase,
      has8Characters,
      hasSpecialCharacter,
      hasNumber,
      allValid: hasLowercaseOrUppercase && has8Characters && hasSpecialCharacter && hasNumber,
    };
  };

  const validateEmail = (email: string) => {
    try {
      const emailSchema = Yup.string()
        .email("Must be a valid email")
        .min(3, "Email must be at least 3 characters")
        .max(50, "Email must be less than 50 characters");
      emailSchema.validateSync(email);
      return { isValid: true, error: undefined };
    } catch (error: any) {
      return { isValid: false, error: error.message || "Email is invalid" };
    }
  };

  /*************
   * USE EFFECTS *
   *************/
  useEffect(() => {
    const referrer = signupSources?.referrer;
    if (!!referrer && referrer !== referralDetails?.referrer) {
      getPartnershipInfo({ referrer })
        .unwrap()
        .then((res) => {
          if (res) {
            dispatch(setReferralDetails({ referrer, name: res.name, rewards: `${res.reward}` }));
          } else {
            dispatch(setReferralDetails({ referrer, name: null, rewards: "between 2-5" }));
          }
        })
        .catch((err) => {
          // if referrer code not found in partnership table, show default message
          dispatch(setReferralDetails({ referrer, name: null, rewards: "between 2-5" }));
        });
    } else if (!referrer) {
      resetReferralDetails();
    }
  }, [signupSources]);

  useEffect(() => {
    if (countdownSeconds === null || countdownSeconds <= 0) {
      setCountdownSeconds(null);
      return;
    }

    const timer = setTimeout(() => {
      setCountdownSeconds(countdownSeconds - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [countdownSeconds]);

  // Cleanup debounceTimer on component unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  /*************
   * EXPORTS *
   *************/
  return {
    togglePasswordVisibility,
    toggleConfirmPasswordVisibility,
    handleCountrySearchChange,
    handleCountryChange,
    handleStateSearchChange,
    handleStateChange,
    handleFirstNameChange,
    handleLastNameChange,
    handleEmailChange,
    handlePasswordChange,
    handleConfirmPasswordChange,
    toggleCountryDropdown,
    toggleStateDropdown,
    handleSubmit,
    handleSubscribeOptionClick,
    handleSubscribeSubmit,
    handleUnsupportedCountryGoBack,
    handlePhoneChange,
    handlePhoneVerificationCodeChange,
    handlePhoneCountryChange,
    togglePhoneCountryDropdown,
    resetReferralDetails,
    handleSubmitPhoneNumber,
    handleResendPhoneNumber,
    handleVerifyPhoneNumber,
    handlePhoneValidationGoBack,
    countdownSeconds,
    handlePhoneInputGoBack,
    validatePassword,
    getPasswordValidationResults,
    resetForm,
    handleSubscribeNotNow,
    // Disclosure flow handlers
    handleDisclosureComplete,
    handleDisclosureClose,
    signupStateRegion,
    signupStateNotRequired,
  };
}
